import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import Swal from "sweetalert2";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { HttpHelpService } from "../service/http-help.service";
import { AddMerchantCategoryModalComponent } from "../../components/shared/add-merchant-category-modal/add-merchant-category-modal.component";

@Component({
  selector: "app-merchant-product-categories",
  templateUrl: "./merchant-product-categories.component.html",
  styleUrls: ["./merchant-product-categories.component.scss"],
})
export class MerchantProductCategoriesComponent implements OnInit, OnDestroy {
  @ViewChild("aiPreviewModal") aiPreviewModal: any;
  @ViewChild("imageViewModal") imageViewModal: any;

  imageViewUrl: string | null = null;

  curentLang = "";
  layoutVersion: any;
  userRegistId: any = JSON.parse(localStorage.getItem("user_id"));
  merchantId = JSON.parse(localStorage.getItem("merchant_id"));
  user_type = JSON.parse(localStorage.getItem("user_type"));
  term = "";
  p: any = 1;

  updateForm: FormGroup;
  categoriesData: any[] = [];
  categoryId: number | null = null;
  updatedData: any;
  viewData: any;
  public validateUpdate = false;

  currentImageUrl: string | null = null;
  aiPreviewUrl: string | null = null;
  aiPreviewKey: string | null = null;
  pendingPreviewKey: string | null = null;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private spinner: NgxSpinnerService,
    public translate: TranslateService,
    private service: HttpHelpService,
    private modalService: NgbModal
  ) {
    this.curentLang = localStorage.getItem("curentLang");
    this.merchantId = JSON.parse(localStorage.getItem("merchant_id"));
    this.user_type = JSON.parse(localStorage.getItem("user_type"));

    this.updateForm = new FormGroup({
      name: new FormControl("", Validators.required),
      code: new FormControl(""),
      sort_order: new FormControl(0),
      generate_ai_image: new FormControl(false),
      ai_image_prompt: new FormControl(""),
    });
  }

  private msg(ar: string, en: string, he: string): string {
    return this.curentLang === "ar" ? ar : this.curentLang === "en" ? en : he;
  }

  private apiMsg(res: any): string {
    return this.curentLang === "ar"
      ? res.messageAr
      : this.curentLang === "en"
        ? res.messageEn
        : res.messageHe;
  }

  categoryPk(item: any): number {
    return Number(item?.merchant_category_id ?? item?.id);
  }

  categoryStatus(item: any): number {
    return Number(item?.status ?? item?.merchant_category_status ?? 1);
  }

  displayName(item: any): string {
    if (!item) return "";
    return this.curentLang === "en"
      ? item.name_en
      : this.curentLang === "ar"
        ? item.name_ar
        : item.name_he;
  }

  onSearchInput(): void {
    this.searchSubject.next(this.term || "");
  }

  private buildImageSaveFields(form: FormGroup) {
    const fields: any = {};
    if (this.pendingPreviewKey) {
      fields.preview_key = this.pendingPreviewKey;
    } else if (form.value.generate_ai_image) {
      fields.generate_ai_image = true;
      const prompt = String(form.value.ai_image_prompt || "").trim();
      if (prompt) fields.ai_image_prompt = prompt;
    }
    return fields;
  }

  private categoryNameForAi(form: FormGroup): string {
    return String(form.value.name || "").trim();
  }

  private buildCategoryNames(form: FormGroup): { name_ar: string; name_en: string; name_he: string } | null {
    const name = String(form.value.name || "").trim();
    if (!name) return null;
    return { name_ar: name, name_en: name, name_he: name };
  }

  openImagePreview(url: string | null | undefined): void {
    if (!url) return;
    this.imageViewUrl = url;
    this.modalService.open(this.imageViewModal, { size: "lg", centered: true });
  }

  openAddCategoryModal(): void {
    const ref = this.modalService.open(AddMerchantCategoryModalComponent, {
      size: "xl",
      centered: true,
    });
    ref.result.then(
      () => this.getAllCategories(),
      () => undefined,
    );
  }

  getAllCategories() {
    this.spinner.show();
    this.service.getMerchantProductCategories(this.term).subscribe({
      next: (res) => {
        this.categoriesData = Array.isArray(res)
          ? res
          : res?.data || res?.categories || [];
        this.spinner.hide();
      },
      error: () => {
        this.spinner.hide();
      },
    });
  }

  getId(id: number) {
    this.categoryId = id;
  }

  setValue(id: number) {
    const item = this.categoriesData.find((c) => this.categoryPk(c) === id);
    if (!item) return;
    const name =
      this.displayName(item) ||
      item.name_he ||
      item.name_ar ||
      item.name_en ||
      "";
    this.updateForm.patchValue({
      name,
      code: item.code || "",
      sort_order: item.sort_order ?? 0,
      generate_ai_image: false,
      ai_image_prompt: "",
    });
    this.currentImageUrl = item.image_url || null;
    this.pendingPreviewKey = null;
    this.aiPreviewUrl = null;
    this.aiPreviewKey = null;
  }

  executeFunctions(id: number, tpl: any) {
    this.updatedData = tpl;
    this.getId(id);
    this.setValue(id);
    this.modalService.open(tpl, { size: "xl", centered: true });
  }

  executeFunctionsView(id: number, tpl: any) {
    this.viewData = tpl;
    this.getId(id);
    this.setValue(id);
    this.modalService.open(tpl, { size: "xl", centered: true });
  }

  updateById() {
    const names = this.buildCategoryNames(this.updateForm);
    if (!names) {
      Swal.fire({
        icon: "warning",
        title: this.msg(
          "يرجى ملء الحقول المطلوبة",
          "Please fill the required fields",
          "אנא מלא את השדות הנדרשים"
        ),
      });
      this.updateForm.markAllAsTouched();
      this.validateUpdate = !this.validateUpdate;
      return;
    }

    const imageFields = this.buildImageSaveFields(this.updateForm);
    const model = {
      ...names,
      code: String(this.updateForm.value.code || "").trim() || undefined,
      sort_order: Number(this.updateForm.value.sort_order) || 0,
      ...imageFields,
    };

    this.spinner.show();
    this.service.updateMerchantProductCategoryById(this.categoryId, model).subscribe((res) => {
      if (res.status === 200) {
        Swal.fire({ icon: "success", title: this.apiMsg(res) });
        this.pendingPreviewKey = null;
        this.modalService.dismissAll();
        this.getAllCategories();
      } else {
        Swal.fire({ icon: "error", title: this.apiMsg(res) });
      }
      this.spinner.hide();
    });
  }

  changeStatus(id: number, status: any) {
    this.spinner.show();
    const model = {
      status: status == 1 ? 59 : 1,
      updated_by: this.userRegistId,
    };
    this.service.changeMerchantProductCategoryStatus(id, model).subscribe({
      next: (res) => {
        if (res.status == 200) {
          Swal.fire({ icon: "success", title: this.apiMsg(res) });
          this.getAllCategories();
        }
        this.spinner.hide();
      },
      error: (err) => {
        Swal.fire({
          icon: "error",
          title: err?.error?.message?.[0] || err?.error?.messageAr || "Error",
        });
        this.spinner.hide();
      },
    });
  }

  onImageSelected(event: any) {
    const file: File = event.target.files?.[0];
    if (!file || !this.categoryId) return;

    const formData = new FormData();
    formData.append("file", file);

    this.spinner.show();
    this.service.uploadMerchantProductCategoryImage(this.categoryId, formData).subscribe({
      next: (res) => {
        this.spinner.hide();
        event.target.value = "";
        const updated = res.data || res;
        if (res.status === 200 || updated.image_url || res.image_url) {
          this.currentImageUrl = updated.image_url || res.image_url || this.currentImageUrl;
          Swal.fire({
            icon: "success",
            title: this.apiMsg(res) || this.msg("تم رفع الصورة", "Image uploaded", "התמונה הועלתה"),
          });
          this.getAllCategories();
        } else {
          Swal.fire({
            icon: "error",
            title: this.apiMsg(res) || this.msg("فشل رفع الصورة", "Image upload failed", "העלאת התמונה נכשלה"),
          });
        }
      },
      error: () => {
        this.spinner.hide();
        event.target.value = "";
        Swal.fire({
          icon: "error",
          title: this.msg("فشل رفع الصورة", "Image upload failed", "העלאת התמונה נכשלה"),
        });
      },
    });
  }

  generateAiImage() {
    const form = this.updateForm;
    const name = this.categoryNameForAi(form);
    const prompt = String(form.value.ai_image_prompt || "").trim();

    if (!name && !prompt) {
      Swal.fire({
        icon: "warning",
        title: this.msg(
          "أدخل اسم التصنيف أو برومبت لتوليد الصورة",
          "Enter a category name or prompt to generate an image",
          "הזן שם קטגוריה או פרומפט כדי ליצור תמונה"
        ),
      });
      return;
    }

    this.spinner.show();
    this.service
      .aiGenerateMerchantProductCategoryImage({
        merchant_id: Number(this.merchantId),
        name: name || undefined,
        prompt: prompt || undefined,
      })
      .subscribe({
        next: (res) => {
          this.spinner.hide();
          const data = res.data || res;
          this.aiPreviewUrl = data.preview_url || null;
          this.aiPreviewKey = data.preview_key || null;
          if (!this.aiPreviewUrl || !this.aiPreviewKey) {
            Swal.fire({
              icon: "error",
              title:
                this.apiMsg(res) ||
                this.msg("فشل توليد الصورة", "AI image generation failed", "יצירת התמונה נכשלה"),
            });
            return;
          }
          this.modalService.open(this.aiPreviewModal, { size: "lg", centered: true });
        },
        error: () => {
          this.spinner.hide();
          Swal.fire({
            icon: "error",
            title: this.msg("فشل توليد الصورة", "AI image generation failed", "יצירת התמונה נכשלה"),
          });
        },
      });
  }

  approveAiImage(modal?: any) {
    if (!this.aiPreviewKey || !this.categoryId) return;

    this.spinner.show();
    this.service
      .approveAiMerchantProductCategoryImage(this.categoryId, {
        preview_key: this.aiPreviewKey,
      })
      .subscribe({
        next: (res) => {
          this.spinner.hide();
          if (res.status === 200 || res.image_url || res.data?.image_url) {
            const updated = res.data || res;
            this.currentImageUrl = updated.image_url || res.image_url || this.currentImageUrl;
            this.pendingPreviewKey = null;
            this.aiPreviewUrl = null;
            this.aiPreviewKey = null;
            if (modal) modal.close();
            Swal.fire({
              icon: "success",
              title: this.apiMsg(res) || this.msg("تم اعتماد الصورة", "Image approved", "התמונה אושרה"),
            });
            this.getAllCategories();
          } else {
            this.pendingPreviewKey = this.aiPreviewKey;
            this.currentImageUrl = this.aiPreviewUrl;
            if (modal) modal.close();
            Swal.fire({
              icon: "info",
              title: this.msg(
                "احفظ التعديل لاعتماد الصورة",
                "Save the update to apply the image",
                "שמור את העריכה כדי להחיל את התמונה"
              ),
            });
          }
        },
        error: () => {
          this.spinner.hide();
          this.pendingPreviewKey = this.aiPreviewKey;
          this.currentImageUrl = this.aiPreviewUrl;
          this.updateForm.patchValue({ generate_ai_image: false });
          if (modal) modal.close();
          Swal.fire({
            icon: "info",
            title: this.msg(
              "احفظ التعديل لاعتماد الصورة",
              "Save the update to apply the image",
              "שמור את העריכה כדי להחיל את התמונה"
            ),
          });
        },
      });
  }

  openVerticallyCentered(dataDeleted: any) {
    this.modalService.open(dataDeleted, { centered: true });
  }

  deleteById() {
    this.spinner.show();
    this.service.deleteMerchantProductCategoryById(this.categoryId).subscribe({
      next: (res) => {
        if (res.status == 400) {
          Swal.fire({ icon: "error", title: this.apiMsg(res) });
          this.spinner.hide();
          return;
        }
        Swal.fire({ icon: "success", title: this.apiMsg(res) });
        this.getAllCategories();
        this.modalService.dismissAll();
        this.spinner.hide();
      },
      error: (err) => {
        Swal.fire({
          icon: "error",
          title: err?.error?.messageAr || err?.message || "Error",
        });
        this.spinner.hide();
      },
    });
  }

  ngOnInit() {
    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.getAllCategories());

    this.getAllCategories();

    this.layoutVersion = localStorage.getItem("layout_version");
    if (this.layoutVersion == null || this.layoutVersion == undefined) {
      localStorage.setItem("layout_version", "light-only");
      this.layoutVersion = "light-only";
    } else if (this.layoutVersion === "dark-only") {
      document.body.classList.add("dark-only");
    } else {
      document.body.classList.add("light-only");
    }

    this.userRegistId = JSON.parse(localStorage.getItem("user_id"));
    this.merchantId = JSON.parse(localStorage.getItem("merchant_id"));
    this.user_type = JSON.parse(localStorage.getItem("user_type"));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
